import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";
import type { DocumentPage } from "@shared/schema";

interface PageNumberWithCommentsProps {
  page: DocumentPage;
  documentColor: string;
  versionLabel?: string;
  pagePosition?: number; // Optional: array position to override page.pageNumber
}

// Status color mapping - reused from existing pattern
const STATUS_COLORS = {
  open: '#f59e0b', // amber-500 equivalent
  inProgress: '#0ea5e9', // sky-500 equivalent
  resolved: '#10b981', // emerald-500 equivalent  
  skipped: '#6b7280', // gray-500 equivalent
} as const;

const NEW_BADGE_DURATION = 5000; // Show badge for 5 seconds

export function PageNumberWithComments({ page, documentColor, versionLabel, pagePosition }: PageNumberWithCommentsProps) {
  const { data: indicators = [] } = useQuery({
    queryKey: ["/api/pages", page.id, "indicators"],
    queryFn: async () => {
      const response = await fetch(`/api/pages/${page.id}/indicators`);
      return response.json();
    },
  });

  // Auto-dismiss NEW badge after time window
  const [showNewBadge, setShowNewBadge] = useState(false);

  useEffect(() => {
    if (page.insertedViaPartialUpload && page.insertedAt) {
      const insertedTime = new Date(page.insertedAt).getTime();
      const now = Date.now();
      const elapsed = now - insertedTime;

      if (elapsed < NEW_BADGE_DURATION) {
        setShowNewBadge(true);
        const remaining = NEW_BADGE_DURATION - elapsed;
        const timer = setTimeout(() => {
          setShowNewBadge(false);
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        setShowNewBadge(false);
      }
    } else {
      setShowNewBadge(false);
    }
  }, [page.insertedViaPartialUpload, page.insertedAt, page.id]);

  // Group indicators by status
  const statusCounts = indicators.reduce((acc: Record<string, number>, indicator: any) => {
    const status = indicator.status || 'open';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const hasIndicators = indicators.length > 0;

  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm font-medium">
          {pagePosition ?? 1}
        </div>
        
        {/* NEW badge for pages inserted via partial upload */}
        {showNewBadge && (
          <div 
            className="px-2 py-1 rounded text-xs font-bold text-white animate-in fade-in duration-300"
            style={{ backgroundColor: documentColor }}
            title="Recently added page"
          >
            NEW
          </div>
        )}
        
        {/* Status-based indicator count badges */}
        {hasIndicators && (
          <div className="flex items-center gap-1">
            {Object.entries(statusCounts).map(([status, count]) => {
              const countNum = count as number;
              return (
                <div 
                  key={status}
                  className="flex items-center gap-1 text-white px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: STATUS_COLORS[status as keyof typeof STATUS_COLORS] || documentColor }}
                  title={`${countNum} ${status} indicator${countNum > 1 ? 's' : ''}`}
                >
                  <MessageSquare className="w-3 h-3" />
                  <span>{countNum}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Version label for compare mode */}
      {versionLabel && (
        <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
          {versionLabel}
        </div>
      )}
    </div>
  );
}